import { useState, useEffect, useCallback } from "react";
import { SPECIALIST_HOSTNAME } from "env-vars";
import { useAdminContent } from "context/admin-content";
import { Patient } from "./types";

// Calculate age from date of birth
function calculateAge(dateOfBirth: string | null): number | null {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

export function usePatient() {
  const adminContent = useAdminContent();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [patient, setPatient] = useState<Patient | null>(null);

  const getPatient = useCallback(async () => {
    setIsLoading(true);

    const patientId = localStorage.getItem("ciq_id");
    const idToken = localStorage.getItem("id_token");
    
    // Request aggregate format to get expanded patient data
    const response = await fetch(
      `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}?format=aggregate`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (response.ok) {
      const { person } = await response.json();

      // Ensure age is calculated if not provided by backend
      if (person && person.age === undefined) {
        person.age = calculateAge(person.date_of_birth);
      }

      setIsLoading(false);

      return person;
    }

    setIsLoading(false);
  }, []);

  const refetch = useCallback(() => {
    setPatient(null);
  }, []);

  useEffect(() => {
    if (adminContent?.isLoading || patient) {
      return;
    }

    (async () => {
      const person = await getPatient();

      if (person) {
        setPatient(person);
      }
    })();
  }, [adminContent?.isLoading, patient, getPatient]);

  return { patient, refetch, isLoading };
}
